import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";

import Layout from "./components/Layout";
import LoadingSpinner from "./components/LoadingSpinner";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import BoxesPage from "./pages/BoxesPage";
import DashboardPage from "./pages/DashboardPage";
import ArrivalsPage from "./pages/ArrivalsPage";
import DeparturesPage from "./pages/DeparturesPage";
import BoxPhotosPage from "./pages/BoxPhotosPage";
import InventoryDetailPage from "./pages/InventoryDetailPage";
import ManualPage from "./pages/ManualPage";
import InventoryListPage from "./pages/InventoryListPage";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ManageTagsPage from "./pages/ManageTagsPage";
import TagStatusPage from "./pages/TagStatusPage";
import MatchTagsPage from "./pages/MatchTagsPage";
import MessagesPage from "./pages/MessagesPage";
import NewScanPage from "./pages/NewScanPage";
import NotFound from "./pages/NotFound";
import BandwidthAlertsPage from "./pages/BandwidthAlertsPage";
import AlertsPage from "./pages/AlertsPage";
import OutagesPage from "./pages/OutagesPage";
import PartsPage from "./pages/PartsPage";
import RecoveryPage from "./pages/RecoveryPage";
import RefillPage from "./pages/RefillPage";
import ReportsPage from "./pages/ReportsPage";
import SopsPage from "./pages/SopsPage";
import SettingsPage from "./pages/SettingsPage";
import SignalDetailPage from "./pages/SignalDetailPage";
import UptimePage from "./pages/UptimePage";
import { configService } from "./services/configService";

const AppRoutes: React.FC = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route
      path="/"
      element={
        <ProtectedRoute>
          <Layout>
            <DashboardPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    {/* /map is an alias for the Dashboard — the map is embedded there
        now (2026-06-03). Kept so old bookmarks and the NotFound link
        still work. */}
    <Route
      path="/map"
      element={
        <ProtectedRoute>
          <Layout>
            <DashboardPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/boxes"
      element={
        <ProtectedRoute>
          <Layout>
            <BoxesPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/outages"
      element={
        <ProtectedRoute cap="operator.triage">
          <Layout>
            <OutagesPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/bandwidth"
      element={
        <ProtectedRoute cap="operator.triage">
          <Layout>
            <BandwidthAlertsPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    {/* Alerts — tabbed operator page (unhealthy tags, more tabs later).
        Platform-only via the dashboard.tag_health cap; deep-linkable per tab. */}
    <Route
      path="/alerts"
      element={
        <ProtectedRoute cap="dashboard.tag_health">
          <Layout>
            <AlertsPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/alerts/:tab"
      element={
        <ProtectedRoute cap="dashboard.tag_health">
          <Layout>
            <AlertsPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/departures"
      element={
        <ProtectedRoute>
          <Layout>
            <DeparturesPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/arrivals"
      element={
        <ProtectedRoute>
          <Layout>
            <ArrivalsPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    {/* ---- /admin/* subpages consolidated into the tabbed /settings page
        (2026-08). Each old URL now redirects to its matching Settings tab
        so existing bookmarks keep working; SettingsPage gates each tab by
        capability internally. ---- */}
    <Route path="/admin/smartboxes" element={<Navigate to="/settings/smartboxes" replace />} />
    <Route path="/admin/users" element={<Navigate to="/settings/users" replace />} />
    <Route path="/admin/roles" element={<Navigate to="/settings/roles" replace />} />
    <Route path="/admin/companies" element={<Navigate to="/settings/companies" replace />} />
    <Route path="/admin/requesters" element={<Navigate to="/settings/requesters" replace />} />
    <Route path="/admin/audit" element={<Navigate to="/settings/audit" replace />} />
    <Route path="/admin/mail" element={<Navigate to="/settings/mail" replace />} />
    <Route path="/admin/report-destinations" element={<Navigate to="/settings/report-destinations" replace />} />
    <Route path="/admin/api-keys" element={<Navigate to="/settings/api-keys" replace />} />
    <Route path="/admin/webhooks" element={<Navigate to="/settings/webhooks" replace />} />
    <Route path="/admin/prices" element={<Navigate to="/settings/prices" replace />} />
    <Route path="/admin/catalog" element={<Navigate to="/settings/catalog" replace />} />
    <Route path="/admin/items" element={<Navigate to="/settings/items" replace />} />
    <Route
      path="/uptime"
      element={
        <ProtectedRoute cap="operator.triage">
          <Layout>
            <UptimePage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/recovery"
      element={
        <ProtectedRoute cap="operator.triage">
          <Layout>
            <RecoveryPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/signal/:scuName"
      element={
        <ProtectedRoute cap="operator.triage">
          <Layout>
            <SignalDetailPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/inventory"
      element={
        <ProtectedRoute>
          <Layout>
            <InventoryListPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    {/* Parts — read-only catalog browser. Scoped server-side via the
        existing /api/catalog endpoint (customer/vendor/platform). */}
    <Route
      path="/parts"
      element={
        <ProtectedRoute>
          <Layout>
            <PartsPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/inventory/:scuName"
      element={
        <ProtectedRoute>
          <Layout>
            <InventoryDetailPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/photos/:scuName"
      element={
        <ProtectedRoute>
          <Layout>
            <BoxPhotosPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/refill"
      element={
        <ProtectedRoute>
          <Layout>
            <RefillPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/manual"
      element={
        <ProtectedRoute>
          <Layout>
            <ManualPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/reports"
      element={
        <ProtectedRoute>
          <Layout>
            <ReportsPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/sops"
      element={
        <ProtectedRoute roles={["platform"]}>
          <Layout>
            <SopsPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/messages"
      element={
        <ProtectedRoute>
          <Layout>
            <MessagesPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    {/* Settings is one tabbed page. No cap on the route — SettingsPage
        gates each tab internally (every user sees Notifications). */}
    <Route
      path="/settings"
      element={
        <ProtectedRoute>
          <Layout>
            <SettingsPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/settings/:tab"
      element={
        <ProtectedRoute>
          <Layout>
            <SettingsPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/tags/match"
      element={
        <ProtectedRoute cap="tags.manage">
          <Layout>
            <MatchTagsPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/tags/manage"
      element={
        <ProtectedRoute cap="tags.manage">
          <Layout>
            <ManageTagsPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/scan/new"
      element={
        <ProtectedRoute cap="reader.vendor_scan">
          <Layout>
            <NewScanPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/tags/status"
      element={
        <ProtectedRoute cap="tags.manage">
          <Layout>
            <TagStatusPage />
          </Layout>
        </ProtectedRoute>
      }
    />
    <Route
      path="*"
      element={
        <Layout>
          <NotFound />
        </Layout>
      }
    />
  </Routes>
);

const App: React.FC = () => {
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    void configService.loadConfig().finally(() => setConfigLoaded(true));
  }, []);

  if (!configLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner label="loading config" />
      </div>
    );
  }

  // CRA's `homepage: "/mttr"` in package.json means the app is served at
  // /mttr/*. Without a basename, BrowserRouter treats /mttr/... pathnames
  // as literal and falls through to NotFound. PUBLIC_URL is what CRA
  // bakes in for the homepage at build time (empty in dev only if homepage
  // is unset — here it's "/mttr" both modes).
  return (
    <AuthProvider>
      <BrowserRouter basename={process.env.PUBLIC_URL || "/"}>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
